/**
 * Lazy-loaded view components for code splitting, and the path -> importer map
 * the boot gate uses to prefetch the initial route's chunk.
 *
 * This file mirrors the exports from ./index.js but wraps each component
 * with lazyWithRetry() for route-level code splitting with automatic
 * retry logic on chunk load failures (e.g. after new deployments).
 *
 * Each view is declared through `lazyRoute`, which does two things with one
 * importer: it returns the lazy component for `Routes.js`, and it records the
 * route paths that render it. `loadRouteChunkFor` then resolves a pathname to
 * that same importer, so `useAppInit` can start the initial route's chunk
 * download in parallel with the session check instead of waiting for auth and
 * user details to resolve first (see `docs/boot-flicker-remediation-plan.md`,
 * step 2).
 *
 * The paths live here, next to the importer, rather than being re-derived from
 * `Routes.js`. A separate table would be free to drift, and a drifted entry
 * fails silently: the gate would release early and the blank frame would come
 * back with nothing to point at.
 *
 * To revert code splitting, change imports in Routes.js from 'views/lazy' back to 'views'.
 */
import { matchPath } from 'react-router-dom';
import lazyWithRetry from 'utils/lazyWithRetry';

/**
 * Every registered route chunk, in declaration order.
 * @type {Array<{paths: string[], importFunction: () => Promise<any>}>}
 */
const routeChunks = [];

/**
 * Declares a lazy view and the route paths that render it.
 *
 * @param {string[]} paths the `path` props `Routes.js` renders this view under.
 *   All of this app's routes are `exact`, so these are matched exactly.
 * @param {() => Promise<any>} importFunction the dynamic import
 * @returns {React.LazyExoticComponent}
 */
const lazyRoute = (paths, importFunction) => {
  routeChunks.push({ paths, importFunction });
  return lazyWithRetry(importFunction);
};

/**
 * Starts the chunk download for whichever view renders `pathname`.
 *
 * Returns the import promise so a caller can wait on it, or `null` when no
 * route matches -- an unknown path falls through to the `not-found` redirect,
 * which has no chunk worth prefetching.
 *
 * The returned promise is the raw `import()`, not `lazyWithRetry`'s retrying
 * wrapper: a caller waiting on this one only needs to know when to stop
 * waiting. `React.lazy` still owns retries and the reload fallback, and
 * because webpack caches the module promise, the component's own factory
 * resolves instantly off this download rather than issuing a second one.
 *
 * @param {string} pathname typically `window.location.pathname`
 * @returns {Promise<any>|null}
 */
export const loadRouteChunkFor = (pathname) => {
  const entry = routeChunks.find(({ paths }) =>
    paths.some((path) => matchPath(pathname, { path, exact: true })),
  );
  return entry ? entry.importFunction() : null;
};

// Restricted views
export const Home = lazyRoute(['/'], () => import('./Restricted/Home'));
export const Unsubscribe = lazyRoute(['/unsubscribe'], () =>
  import('./Restricted/Unsubscribe'),
);
export const NotFoundCover = lazyRoute(['/not-found-cover'], () =>
  import('./Restricted/NotFoundCover'),
);
export const SomethingWentWrong = lazyRoute(['/something-went-wrong'], () =>
  import('./Restricted/SomethingWentWrong'),
);
export const ProfileSettings = lazyRoute(['/profile'], () =>
  import('./Restricted/ProfileSettings'),
);
export const Preferences = lazyRoute(['/preferences'], () =>
  import('./Restricted/Preferences'),
);
export const OrgSettings = lazyRoute(['/org-settings'], () =>
  import('./Restricted/OrgSettings'),
);
export const SalesOrg = lazyRoute(['/sales-org'], () =>
  import('./Restricted/SalesOrg'),
);
export const Integrations = lazyRoute(['/integrations'], () =>
  import('./Restricted/Integrations'),
);
export const TeamSettings = lazyRoute(['/manage-members'], () =>
  import('./Restricted/TeamSettings'),
);
export const Pricing = lazyRoute(['/pricing'], () =>
  import('./Restricted/Pricing'),
);
export const GoogleCalendarConnect = lazyRoute(['/oauth/google'], () =>
  import('./Restricted/GoogleCalendarConnect'),
);
export const ZohoCrmConnect = lazyRoute(['/oauth/zoho-crm'], () =>
  import('./Restricted/ZohoCrmConnect'),
);
export const PrivacyPolicy = lazyRoute(['/privacy', '/tos'], () =>
  import('./Restricted/PrivacyPolicy'),
);
export const GmailIntegrationHelp = lazyRoute(
  ['/google-oauth-integration-help'],
  () => import('./Restricted/GmailIntegrationHelp'),
);
export const OpenAiIntegrationHelp = lazyRoute(
  ['/openai-integration-help'],
  () => import('./Restricted/OpenAiIntegrationHelp'),
);
export const DealPage = lazyRoute(['/deals/:dealId'], () =>
  import('./Restricted/DealPage'),
);
export const FeaturePreview = lazyRoute(['/fp/:uuid'], () =>
  import('./Restricted/FeaturePreview'),
);
export const RecallAlternative = lazyRoute(['/m-record'], () =>
  import('./Restricted/RecallAlternative'),
);
export const DeepLinkValidation = lazyRoute(['/interaction'], () =>
  import('./Restricted/DeepLinkValidation'),
);
export const GmailOAuthConnect = lazyRoute(['/oauth/google/gmail'], () =>
  import('./Restricted/GmailOAuthConnect'),
);
export const OutlookCalendarOAuthConnect = lazyRoute(
  ['/oauth/outlook/calendar'],
  () => import('./Restricted/OutlookCalendarOAuthConnect'),
);
export const OutlookEmailOAuthConnect = lazyRoute(
  ['/oauth/outlook/email'],
  () => import('./Restricted/OutlookEmailOAuthConnect'),
);
export const PipedriveOAuthConnect = lazyRoute(['/oauth/pipedrive-crm'], () =>
  import('./Restricted/PipedriveOAuthConnect'),
);
export const ZoomOAuthConnect = lazyRoute(['/oauth/zoom'], () =>
  import('./Restricted/ZoomOAuthConnect'),
);
export const UnipileOAuthConnect = lazyRoute(
  ['/integrations/unipile/success', '/integrations/unipile/failure'],
  () => import('./Restricted/UnipileOAuthConnect'),
);
export const SlackOAuthConnect = lazyRoute(['/oauth/slack'], () =>
  import('./Restricted/SlackOAuthConnect'),
);
export const OAuthLogin = lazyRoute(['/oauth/login'], () =>
  import('./Restricted/OAuthLogin'),
);
export const OAuthConsent = lazyRoute(['/oauth/consent'], () =>
  import('./Restricted/OAuthConsent'),
);

// Auth views
export const SigninCover = lazyRoute(['/auth/signin'], () =>
  import('./Auth/SigninCover'),
);
export const SignupCover = lazyRoute(['/auth/signup'], () =>
  import('./Auth/SignupCover'),
);
export const ResetPassword = lazyRoute(['/reset/password'], () =>
  import('./Auth/ResetPassword'),
);
export const FirebaseEmailAction = lazyRoute(['/email/action'], () =>
  import('./Auth/FirebaseEmailAction'),
);

// Candidate views
export const ActionPlan = lazyRoute(['/action-plan'], () =>
  import('./Restricted/ActionPlan'),
);
export const MeetingPrepPublic = lazyRoute(['/meeting-prep'], () =>
  import('./Restricted/MeetingPrepPublic'),
);
export const Deals = lazyRoute(['/deals'], () => import('./Restricted/Deals'));
export const UnlinkedItems = lazyRoute(['/unlinked-items'], () =>
  import('./Restricted/UnlinkedItems'),
);
export const Mailbox = lazyRoute(['/mailbox'], () =>
  import('./Restricted/Mailbox'),
);
export const ExpertConversation = lazyRoute(
  ['/digital-twin', '/ask-ai', '/conversation-ai'],
  () => import('./Restricted/ExpertConversation'),
);
export const Companies = lazyRoute(['/companies'], () =>
  import('./Restricted/Companies'),
);
export const CompanyPage = lazyRoute(['/companies/:companyId'], () =>
  import('./Restricted/CompanyPage'),
);
export const Contacts = lazyRoute(['/contacts'], () =>
  import('./Restricted/Contacts'),
);
export const ContactPage = lazyRoute(['/contacts/:contactId'], () =>
  import('./Restricted/ContactPage'),
);
export const Reports = lazyRoute(['/reports'], () =>
  import('./Restricted/Reports'),
);
